/**
 * AlertExpandedPanel
 * Main expanded-row component for the Alerts DataTable.
 *
 * Assembles:
 *   1. AlertHeaderMeta    – type icon + name/level + status badge
 *   2. AlertTimingCard    – 6 datetime fields in a grid
 *   3. AlertFlowSection   – activity stepper (handles all loading/empty states)
 *   4. AlertMoreDetails   – collapsible activity timeline + partner/client cards
 */
import React from 'react';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import type { IAlertProps } from '@/pages/Alert/alerts.interface';
import type { ISingleAlert } from '@/services/alert/alerts.type';
import {
  providerAlertStepperConfig,
  parseAlertStepperData,
  AlertStepActionStatus,
} from '@/constant/alerts/alerts.constant';
import { COMMON_STEPPER_CONTENT } from '@/constant/global/global.constant';
import { tid } from '@/utils/testId';
import { ALERT_TEST_IDS as TID } from '@/constant/testIds/alert.testIds';
import { AlertHeaderMeta } from './AlertHeaderMeta';
import { AlertTimingCard } from './AlertTimingCard';
import { AlertFlowSection } from './AlertFlowSection';
import { AlertMoreDetails } from './AlertMoreDetails';

export interface AlertExpandedPanelProps {
  data: IAlertProps;
  rowIndex?: number;
  /** kept for API compatibility — not used directly */
  missingAttributes?: any;
  singleAlertData: ISingleAlert;
  loading: boolean;
}

export const AlertExpandedPanel = ({
  data,
  rowIndex,
  singleAlertData,
  loading,
}: AlertExpandedPanelProps) => {
  const {
    providerId,
    partnerUid,
    triggeredOn,
    sentClient,
    sentOn,
    createdOn,
    closedOn,
    type = '-',
    level = '-',
    status = '-',
  } = data;

  // ── Single-alert detail ──────────────────────────────────────────────────
  const { activity: { activityLog, client, partner } = {} } =
    singleAlertData || {};

  // ── Provider alert stepper config ────────────────────────────────────────
  const providerArray = useSelector(
    (state: any) => state?.provider?.providers?.data,
  );
  const alertsConfig = providerAlertStepperConfig(providerArray, partnerUid);

  // ── Stepper data ─────────────────────────────────────────────────────────
  const { ENABLED_ALERT_STEPS } = alertsConfig;
  const alertStepperData = parseAlertStepperData(ENABLED_ALERT_STEPS ?? [], {
    alertLog: activityLog,
  });
  const { eachStepSummary } = alertStepperData;

  const isHavingClientFlow =
    ENABLED_ALERT_STEPS?.some(
      (s: any) => s.stepTitle === COMMON_STEPPER_CONTENT.STEPPER_TITLES.CLIENTS,
    ) ?? false;
  const isHavingPartnerFlow =
    ENABLED_ALERT_STEPS?.some(
      (s: any) => s.stepTitle === COMMON_STEPPER_CONTENT.STEPPER_TITLES.VMS,
    ) ?? false;

  const isActivityLogDataReceived =
    !_.isNil(activityLog) && (activityLog?.length ?? 0) > 0;

  return (
    <div data-testid={tid(TID.EXPANDED_PANEL)} className="space-y-3">
      {/* 1 ── Header: type icon + name/level + status ───────────── */}
      <AlertHeaderMeta
        type={String(type)}
        level={String(level)}
        status={String(status)}
        testId={tid(TID.EXPANDED_HEADER)}
      />

      {/* 2 ── Timing grid ─────────────────────────────────────── */}
      <AlertTimingCard data={data} testId={tid(TID.EXPANDED_TIMING)} />

      {/* 3 ── Activity Flow stepper ───────────────────────────── */}
      <AlertFlowSection
        rowIndex={rowIndex}
        loading={loading}
        isActivityLogDataReceived={isActivityLogDataReceived}
        activityLog={activityLog}
        triggeredOn={triggeredOn}
        sentClient={sentClient}
        sentOn={sentOn}
        createdOn={createdOn}
        closedOn={closedOn}
        alertsConfig={alertsConfig}
        providerData={{ partnerUid, providerId }}
        alertType={data?.type}
        testId={tid(TID.EXPANDED_FLOW)}
      />

      {/* 4 ── "View more details" toggle ──────────────────────── */}
      <AlertMoreDetails
        isActivityLogDataReceived={isActivityLogDataReceived}
        activityLog={activityLog}
        isHavingClientFlow={isHavingClientFlow}
        isHavingPartnerFlow={isHavingPartnerFlow}
        partner={partner ?? null}
        client={client ?? null}
        eachStepSummary={
          eachStepSummary as Record<string, AlertStepActionStatus>
        }
        toggleTestId={tid(TID.EXPANDED_MORE_TOGGLE)}
        contentTestId={tid(TID.EXPANDED_MORE_CONTENT)}
        timelineTestId={tid(TID.EXPANDED_TIMELINE)}
      />
    </div>
  );
};
